import createApiInstance from './config';

// Create an axios instance for activity endpoints
const api = createApiInstance();

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Cache to prevent duplicate requests
const requestCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 30000; // 30 seconds

// In-flight request deduplication
const pendingRequests = new Map<string, Promise<any>>();

const getCachedData = (key: string) => {
  const cached = requestCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  requestCache.set(key, { data, timestamp: Date.now() });
};

/**
 * Deduplicated fetch: returns in-flight promise if same request is already pending
 */
const deduplicatedGet = async <T>(url: string, cacheKey: string): Promise<T> => {
  // Check cache first
  const cached = getCachedData(cacheKey);
  if (cached) return cached;

  // Check if request is already in-flight
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey)!;
  }

  // Start new request
  const promise = api.get(url)
    .then(response => {
      setCachedData(cacheKey, response.data);
      return response.data;
    })
    .finally(() => {
      pendingRequests.delete(cacheKey);
    });

  pendingRequests.set(cacheKey, promise);
  return promise;
};

// Activity metrics interface
export interface ActivityMetrics {
  total_activities: number;
  calls_attempted: number;
  calls_reached: number;
  emails_sent: number;
  sms_sent: number;
  other_activities: number;
  leads_contacted: number;
  us_activities: number;
  other_countries_activities: number;
  us_leads_contacted: number;
  other_countries_leads_contacted: number;
  leads_created: number;
  leads_assigned: number;
}

// Daily chart data interface
export interface DailyChartData {
  date: string;
  calls_attempted: number;
  calls_reached: number;
  emails_sent: number;
  sms_sent: number;
  other_activities: number;
}

// Stage counts interface
export interface StageCounts {
  current_stage: string;
  leads_contacted: number;
  activities: number;
}

// Filters applied interface
export interface FiltersApplied {
  user_id: number;
  start_date: string;
  end_date: string;
  in_report: boolean | null;
}

// Activity stats response interface
export interface ActivityStatsResponse {
  activity_metrics: ActivityMetrics;
  daily_chart_data: DailyChartData[];
  stage_counts: StageCounts[];
  filters_applied: FiltersApplied;
}

// Individual activity interface
export interface Activity {
  id: number;
  lead_company_id: number;
  lead_contact_id: number;
  activity_type: string;
  activity_status: string;
  activity_description: string;
  activity_date: string;
  schedule_date: string | null;
  duration_minutes: number | null;
  outcome: string | null;
  ai_summary: string | null;
  created_by: number;
  created_at: string;
  updated_at: string;
}

// Activities list response interface
export interface ActivitiesResponse {
  activities: Activity[];
}

// Get activity stats for a user
export const getUserActivityStats = async (
  userId: number,
  startDate?: string,
  endDate?: string,
  inReport?: boolean
): Promise<ActivityStatsResponse> => {
  const params = new URLSearchParams({ user_id: userId.toString() });
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (inReport !== undefined) params.append('in_report', inReport.toString());
  
  const cacheKey = `activity-stats-${userId}-${params.toString()}`;

  try {
    const url = `/activity/user/${userId}/stats?${params}`;
    return await deduplicatedGet<ActivityStatsResponse>(url, cacheKey);
  } catch (error: any) {
    console.error('🔥 ERROR fetching activity stats:', {
      error: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: `${api.defaults.baseURL}/activity/user/${userId}/stats?${params}`
    });
    // Return fallback data
    const fallbackData: ActivityStatsResponse = {
      activity_metrics: {
        total_activities: 0,
        calls_attempted: 0,
        calls_reached: 0,
        emails_sent: 0,
        sms_sent: 0,
        other_activities: 0,
        leads_contacted: 0,
        us_activities: 0,
        other_countries_activities: 0,
        us_leads_contacted: 0,
        other_countries_leads_contacted: 0,
        leads_created: 0,
        leads_assigned: 0
      },
      daily_chart_data: [],
      stage_counts: [],
      filters_applied: {
        user_id: userId,
        start_date: startDate || new Date().toISOString().split('T')[0],
        end_date: endDate || new Date().toISOString().split('T')[0],
        in_report: inReport || null
      }
    };
    setCachedData(cacheKey, fallbackData);
    return fallbackData;
  }
};

// Get recent activities for a user
export const getUserActivities = async (
  userId: number,
  startDate?: string,
  endDate?: string,
  limit: number = 500,
  offset: number = 0
): Promise<ActivitiesResponse> => {
  const params = new URLSearchParams({ 
    limit: limit.toString(),
    offset: offset.toString()
  });
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  
  const cacheKey = `user-activities-${userId}-${params.toString()}`;

  try {
    const url = `/activity/user/${userId}?${params}`;
    return await deduplicatedGet<ActivitiesResponse>(url, cacheKey);
  } catch (error: any) {
    console.error('🔥 ERROR fetching user activities:', {
      error: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: `${api.defaults.baseURL}/activity/user/${userId}?${params}`
    });
    // Return fallback data
    const fallbackData: ActivitiesResponse = {
      activities: []
    };
    setCachedData(cacheKey, fallbackData);
    return fallbackData;
  }
};

// Get today's activity stats for a user
export const getTodayActivityStats = async (userId: number): Promise<ActivityStatsResponse> => {
  const today = new Date().toISOString().split('T')[0];
  return getUserActivityStats(userId, today, today);
};

// Get recent activities (last 10)
export const getRecentUserActivities = async (userId: number, limit: number = 10): Promise<ActivitiesResponse> => {
  return getUserActivities(userId, undefined, undefined, limit, 0);
};

// Export a consolidated activity API object
export const activityApi = {
  getUserActivityStats,
  getUserActivities,
  getTodayActivityStats,
  getRecentUserActivities
};